<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Score de Qualidade',
  },
});

const components = computed(() => props.data?.components || {});
const details = computed(() => props.data?.details || {});

const componentList = [
  { key: 'csat', label: 'CSAT', icon: 'i-lucide-star', color: 'bg-n-amber-9' },
  {
    key: 'sla',
    label: 'SLA',
    icon: 'i-lucide-shield-check',
    color: 'bg-n-teal-9',
  },
  {
    key: 'response_time',
    label: 'Tempo Resposta',
    icon: 'i-lucide-timer',
    color: 'bg-n-blue-9',
  },
  {
    key: 'resolution',
    label: 'Resolucao',
    icon: 'i-lucide-check-circle',
    color: 'bg-n-violet-9',
  },
];

function getGradeColor(grade) {
  const colors = {
    A: 'bg-n-teal-9 text-white',
    B: 'bg-n-blue-9 text-white',
    C: 'bg-n-amber-9 text-white',
    D: 'bg-n-orange-9 text-white',
    F: 'bg-n-ruby-9 text-white',
  };
  return colors[grade] || colors.F;
}

function getScoreColor(score) {
  if (score >= 80) return 'text-n-teal-11';
  if (score >= 60) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-award text-lg text-n-violet-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <span
          class="px-3 py-1 rounded-lg text-xl font-bold"
          :class="getGradeColor(data.quality_grade)"
        >
          {{ data.quality_grade || 'F' }}
        </span>
        <span
          class="text-2xl font-bold"
          :class="getScoreColor(data.quality_score)"
        >
          {{ data.quality_score || 0 }}
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Components Breakdown -->
      <div class="flex flex-col gap-3">
        <span class="text-sm font-medium text-n-slate-12">Componentes do Score</span>
        <div
          v-for="comp in componentList"
          :key="comp.key"
          class="flex flex-col gap-1"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span :class="comp.icon" class="text-n-slate-10" />
              <span class="text-sm text-n-slate-12">{{ comp.label }}</span>
              <span class="text-xs text-n-slate-10">({{ components[comp.key]?.weight || 0 }}%)</span>
            </div>
            <span
              class="text-sm font-medium"
              :class="getScoreColor(components[comp.key]?.score)"
            >
              {{ components[comp.key]?.score || 0 }}
            </span>
          </div>
          <div class="h-2 bg-n-alpha-2 rounded-full overflow-hidden">
            <div
              class="h-full rounded-full transition-all duration-300"
              :class="comp.color"
              :style="{ width: `${components[comp.key]?.score || 0}%` }"
            />
          </div>
        </div>
      </div>

      <!-- Details -->
      <div class="grid grid-cols-2 gap-3 pt-3 border-t border-n-weak">
        <div class="flex flex-col items-center p-2 rounded-lg bg-n-alpha-1">
          <span class="text-lg font-bold text-n-slate-12">{{
            details.csat_average || 0
          }}</span>
          <span class="text-xs text-n-slate-11">CSAT Medio (1-5)</span>
        </div>
        <div class="flex flex-col items-center p-2 rounded-lg bg-n-alpha-1">
          <span class="text-lg font-bold text-n-slate-12">{{
            details.csat_responses || 0
          }}</span>
          <span class="text-xs text-n-slate-11">Respostas CSAT</span>
        </div>
        <div class="flex flex-col items-center p-2 rounded-lg bg-n-alpha-1">
          <span class="text-lg font-bold text-n-teal-11">{{ details.sla_compliance || 0 }}%</span>
          <span class="text-xs text-n-slate-11">Conformidade SLA</span>
        </div>
        <div class="flex flex-col items-center p-2 rounded-lg bg-n-alpha-1">
          <span class="text-lg font-bold text-n-blue-11">{{ details.avg_response_minutes || 0 }} min</span>
          <span class="text-xs text-n-slate-11">Tempo Medio Resp.</span>
        </div>
      </div>

      <!-- Resolution Stats -->
      <div
        class="flex items-center justify-between text-xs text-n-slate-11 pt-2 border-t border-n-weak"
      >
        <span>{{ details.conversations_resolved || 0 }} conversas resolvidas</span>
        <span>{{ details.resolution_rate || 0 }}% taxa de resolucao</span>
      </div>
    </div>
  </div>
</template>
